'use client';

import ModalResponsive, { ModalResponsiveFooter } from '@/components/commons/modal-responsive';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import CardPromotion from '@/components/sections/builder/elements/body/cart-total/component/card-promotion';
import { EmptyVoucher } from '@/components/sections/builder/elements/body/cart-total/component/empty-voucher';
import { Button } from '@/components/ui/button';
import { useMutateValidateVoucher } from '@/hooks/api/storefront/promotion';
import { calculatePercentage, computeDisabled } from '@/lib/promotions';
import { cn, isEmpty, parseErrorMessage } from '@/lib/utils';
import { ThemePromotionType } from '@/stores/builder';
import { usePromotionStore } from '@/stores/promotion';
import { StoreCart } from '@/types/http';
import { BasePromotion } from '@/types/http/promotion/common';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';

type ModalPromotionProps = {
  openModal: boolean;
  setOpenModalAction: (open: boolean) => void;
  grandTotal: number;
  listPromotion: BasePromotion[] | undefined;
  onApplyVoucherAction: (selectedVoucherIds: string[]) => void;
  onRedeemVoucherAction: (promo: BasePromotion[], code: string) => void;
  token: string;
  data: StoreCart;
  theme?: ThemePromotionType;
  preview?: boolean;
};

const cssVarsFromTheme = (theme?: ThemePromotionType): React.CSSProperties => {
  const colorScheme = theme?.colorScheme;
  return {
    '--ring-active-color': colorScheme?.background || '#000000',
    '--btn-label': colorScheme?.btnSolidlabel || '#ffffff',
    '--btn-background': colorScheme?.btnSolidBackgroud || '#000000',
    '--text-primary': colorScheme?.text || '#000000',
    '--text-secondary': colorScheme?.textSecondary || '#6b7280',
  } as React.CSSProperties;
};

export default function ModalPromotion({
  openModal,
  setOpenModalAction: setOpenModal,
  grandTotal,
  listPromotion,
  onApplyVoucherAction: onApplyVoucher,
  onRedeemVoucherAction: onRedeemVoucher,
  token,
  data,
  theme,
  preview = false,
}: ModalPromotionProps) {
  const [selectedPromo, setSelectedPromo] = useState<string[]>([]);
  const [voucherCode, setVoucherCode] = useState<string>('');
  const [errorVoucher, setErrorVoucher] = useState<string | string[] | null>(null);

  const inputId = useId();

  const selectedPromotion = usePromotionStore((s) => s.selectedPromotion);

  const promotions = useMemo(() => listPromotion || [], [listPromotion]);

  const validateVoucher = useMutateValidateVoucher({
    onSuccess: (res) => {
      const promoRewards = res.data?.promotion_rewards ?? [];
      const isValid = !!res.data?.is_valid;

      if (isValid && promoRewards?.length) {
        const code = voucherCode;
        setVoucherCode('');
        setErrorVoucher(null);
        setOpenModal(false);
        onRedeemVoucher(promoRewards, code);
      } else {
        setErrorVoucher('Voucher is not valid');
      }
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        setErrorVoucher(error.response?.data?.message ?? 'Voucher validation failed');
      } else {
        setErrorVoucher('Voucher validation failed');
      }
    },
  });

  const handleOnSelectPromo = useCallback((promoId: string, isOverlap: boolean) => {
    setSelectedPromo((prev) => {
      const already = prev.includes(promoId);
      if (already) return prev.filter((id) => id !== promoId);
      if (!isOverlap) return [promoId];
      return [...prev, promoId];
    });
  }, []);

  const handleOnSubmitRedeem = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (preview) return;

      const code = voucherCode.trim();
      if (!code) {
        setErrorVoucher('Please enter the voucher code');
        return;
      }

      if ((selectedPromotion ?? []).some((p) => p.voucher_code === code)) {
        setErrorVoucher('Voucher code already used');
        return;
      }

      const payload = {
        voucher_code: code,
        order: {
          items:
            data?.items?.map((item: { id: string; quantity: number }) => ({
              item_id: item.id,
              qty: item.quantity,
            })) ?? [],
          grand_total: grandTotal || 0,
        },
      };

      try {
        await validateVoucher.mutateAsync({ body: payload, token });
      } catch (err) {
        if (isAxiosError(err)) {
          const rawMessage = err.response?.data?.data ?? err.response?.data?.message;

          try {
            const parsed = typeof rawMessage === 'string' ? JSON.parse(rawMessage) : rawMessage;

            if (Array.isArray(parsed)) {
              setErrorVoucher(parsed.map((raw) => raw.message).filter(Boolean));
            } else {
              setErrorVoucher(parsed?.message || 'An error occurred while processing the voucher');
            }
          } catch {
            setErrorVoucher(
              parseErrorMessage(rawMessage) || 'An error occurred while processing the voucher'
            );
          }
        } else {
          setErrorVoucher('An error occurred while processing the voucher');
        }
      }
    },
    [voucherCode, preview, selectedPromotion, data?.items, grandTotal, token, validateVoucher]
  );

  useEffect(() => {
    if (openModal) {
      const ids = (selectedPromotion ?? []).map((promo) => promo.promotion_id);
      setSelectedPromo(ids);
    } else {
      setSelectedPromo([]);
      setErrorVoucher(null);
    }
  }, [openModal, selectedPromotion]);

  useEffect(() => {
    if (!errorVoucher) return;
    const t = window.setTimeout(
      () => setErrorVoucher(null),
      Array.isArray(errorVoucher) ? 8000 : 5000
    );
    return () => window.clearTimeout(t);
  }, [errorVoucher]);

  const footerActionDisabled = preview || selectedPromo.length === 0 || promotions.length === 0;

  const wrapperVars = useMemo(() => cssVarsFromTheme(theme), [theme]);
  const useVariant = !isEmpty(theme?.colorScheme);

  return (
    <ModalResponsive
      openModal={openModal}
      title={`Select Voucher ${
        selectedPromo.length > 0 ? `(${selectedPromo.length} selected)` : ''
      }`}
      setOpenModal={setOpenModal}
      className='z-[9990] max-w-xl !rounded-none'
    >
      <form
        className='flex flex-col bg-white px-7'
        style={wrapperVars}
        onSubmit={handleOnSubmitRedeem}
      >
        <div className='flex w-full items-center gap-2'>
          <div className='flex w-full gap-2'>
            <label htmlFor={inputId} className='sr-only'>
              Voucher Code
            </label>
            <input
              id={inputId}
              type='text'
              placeholder='Enter voucher code'
              className={cn(
                'w-full flex-1 rounded p-2 text-xs outline-none ring-1 focus:ring-1',
                errorVoucher
                  ? 'ring-red-500'
                  : 'ring-gray-200 focus:ring-[var(--ring-active-color)]'
              )}
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value)}
              aria-invalid={!!errorVoucher}
              aria-describedby={errorVoucher ? `${inputId}-error` : undefined}
              autoComplete='off'
            />

            <Button
              type='submit'
              variant={useVariant ? null : 'secondary'}
              className={cn(
                'flex items-center py-5 text-xs font-normal transition-all duration-300 hover:opacity-80',
                useVariant && 'bg-[--btn-background] text-[--btn-label]'
              )}
              disabled={validateVoucher.isPending || preview}
            >
              {validateVoucher.isPending && <LoadingAnimationIcon size={4} />} Redeem
            </Button>
          </div>
        </div>
        {Array.isArray(errorVoucher) ? (
          <ul className='ml-4 mt-1 list-disc text-red-500'>
            {errorVoucher.map((err, i) => (
              <li key={i} className='text-xs' role='alert' aria-live='polite'>
                {err}
              </li>
            ))}
          </ul>
        ) : errorVoucher ? (
          <p
            id={`${inputId}-error`}
            className='mt-1 text-xs text-red-500'
            role='alert'
            aria-live='polite'
          >
            {errorVoucher}
          </p>
        ) : null}
      </form>

      <div className='flex max-h-[350px] flex-col gap-3 px-7 py-2'>
        <div className='space-y-3 overflow-y-auto pr-2'>
          {(listPromotion ?? []).length > 0 ? (
            listPromotion?.map((promo) => {
              const isSelected = selectedPromo.includes(promo.promotion_id);
              const isDisabled = computeDisabled(promo, selectedPromo, promotions, grandTotal);
              const percentage = calculatePercentage(grandTotal, promo.minimum_spending);
              const isEligiblePromo = grandTotal >= (promo.minimum_spending || 0);

              return (
                <CardPromotion
                  key={promo.promotion_id}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                  percentage={percentage}
                  promo={promo}
                  isEligiblePromo={isEligiblePromo}
                  grandTotal={grandTotal}
                  handleSelectPromo={handleOnSelectPromo}
                  theme={theme}
                  preview={preview}
                />
              );
            })
          ) : (
            <EmptyVoucher />
          )}
        </div>
      </div>

      <ModalResponsiveFooter
        className='flex items-center !justify-between text-sm'
        style={wrapperVars}
      >
        <div className='flex w-full justify-end gap-2'>
          <Button
            variant='outline'
            onClick={() => setOpenModal(false)}
            className='text-xs font-normal'
          >
            Cancel
          </Button>
          <Button
            variant={useVariant ? null : 'default'}
            className={cn(
              'text-xs font-normal transition-all duration-300 hover:opacity-80',
              useVariant && 'bg-[--btn-background] text-[--btn-label]'
            )}
            onClick={() => {
              onApplyVoucher(selectedPromo);
              setOpenModal(false);
            }}
            disabled={footerActionDisabled}
          >
            Use Voucher
          </Button>
        </div>
      </ModalResponsiveFooter>
    </ModalResponsive>
  );
}
